/**
 * Styles - Generates CSS for voice interface
 * 
 * Required from parent (voiceInterface):
 * - config: { avatarBackgroundColor, statusTitleColor, ... }
 * - config.useShadowDOM: boolean
 * 
 * State owned by this module:
 * - None (stateless CSS generation)
 */

export class Styles {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
  }

  /**
   * Generate CSS for voice interface - Optimized for small heights
   */
  generateCSS() {
    const avatarBg = this.config.avatarBackgroundColor || '#667eea';
    const avatarActiveBg = this.config.avatarActiveBackgroundColor || this.config.avatarBackgroundColor || '#667eea';
    const statusTitleColor = this.config.statusTitleColor || '#1e293b';
    const statusSubtitleColor = this.config.statusSubtitleColor || '#64748b';
    const statusDotColor = this.config.statusDotColor || '#10b981';
    const statusText = this.config.statusText || null; // Custom status text (null = use translations)
    const startCallBtnColor = this.config.startCallButtonColor || '#667eea';
    const startCallBtnTextColor = this.config.startCallButtonTextColor || '#FFFFFF';
    const transcriptBg = this.config.transcriptBackgroundColor || '#FFFFFF';
    const transcriptTextColor = this.config.transcriptTextColor || '#1e293b';
    const transcriptLabelColor = this.config.transcriptLabelColor || '#94a3b8';
    const controlBtnColor = this.config.controlButtonColor || '#FFFFFF';
    const liveTranscriptTextColor = this.config.liveTranscriptTextColor || this.config.transcriptTextColor || '#64748b';
    const liveTranscriptFontSize = this.config.liveTranscriptFontSize || '14px';
    const controlBtnSecondaryColor = this.config.controlButtonSecondaryColor || '#64748b';
    const liveIndicatorDotColor = this.config.liveIndicatorDotColor || '#10b981';
    const liveIndicatorTextColor = this.config.liveIndicatorTextColor || '#10b981';
    const micButtonColor = this.config.micButtonColor || controlBtnColor;
    const speakerButtonColor = this.config.speakerButtonColor || controlBtnColor;
    const endCallBtnColor = this.config.endCallButtonColor || '#ef4444';
    const voiceSendButtonColor = this.config.sendButtonColor || this.config.text?.sendButtonColor || this.config.voiceSendButtonColor || '#7C3AED';
    const important = ' !important';
    
    return `
      /* Voice Interface Styles - Ultra-compact for Wix iframes */
      .voice-interface { 
        display: none${important}; 
        flex: 1; 
        flex-direction: column;
        align-items: stretch; 
        justify-content: flex-start; 
        padding: 0; 
        background: linear-gradient(180deg, #f8fafc 0%, #e0e7ff 100%);
        overflow: hidden;
        min-height: 0;
        height: 100%;
        width: 100%;
        box-sizing: border-box;
      }
      .voice-interface.active { display: flex${important}; }
      
      /* Voice States - Better scaling gaps */
      #voiceIdleState {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        height: 100%;
        min-height: 0;
        justify-content: center;
        overflow: hidden;
        flex: 1;
        box-sizing: border-box;
        gap: clamp(6px, 1.5vh, 12px);
      }
      
      #voiceActiveState {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        height: 100%;
        min-height: 0;
        justify-content: flex-start;
        overflow: hidden;
        flex: 1;
        box-sizing: border-box;
        gap: 0;
        padding: 0;
      }
      
      #voiceActiveState[style*="display: none"] {
        display: none !important;
      }
      
      /* Voice Avatar - Scales better from small to large */
      .voice-avatar,
      .voice-avatar-active {
        width: clamp(80px, 18vh, 160px);
        height: clamp(80px, 18vh, 160px);
        aspect-ratio: 1;
        border-radius: 50%;
        background: linear-gradient(135deg, ${avatarBg} 0%, ${avatarActiveBg} 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: clamp(40px, 9vh, 80px);
        margin: 0;
        box-shadow: 0 8px 30px rgba(102, 126, 234, 0.3);
        transition: all 0.3s ease;
        flex-shrink: 0;
        position: relative;
      }
      
      .voice-avatar-active {
        animation: avatarPulse 2s ease-in-out infinite;
      }
      
      @keyframes avatarPulse {
        0%, 100% { 
          transform: scale(1);
        }
        50% { 
          transform: scale(1.05);
        }
      }
      
      .voice-avatar-active.speaking {
        animation: avatarSpeak 0.5s ease-in-out infinite;
      }
      
      @keyframes avatarSpeak {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.08); }
      }
      
      /* Voice Rings Animation */
      .voice-rings {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
      }
      
      .voice-ring {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border: 2px solid rgba(102, 126, 234, 0.3);
        border-radius: 50%;
        opacity: 0;
        animation: ringExpand 2s ease-out infinite;
      }
      
      .voice-ring:nth-child(2) { animation-delay: 0.5s; }
      .voice-ring:nth-child(3) { animation-delay: 1s; }
      
      @keyframes ringExpand {
        0% {
          width: 100%;
          height: 100%;
          opacity: 0.6;
        }
        100% {
          width: 160%;
          height: 160%;
          opacity: 0;
        }
      }
      
      /* Voice Status - Scales with container */
      .voice-status {
        text-align: center;
        margin: 0;
        flex-shrink: 0;
        min-height: 0;
      }
      
      .voice-status-title {
        font-size: clamp(12px, 3vh, 20px);
        font-weight: 600;
        color: ${statusTitleColor};
        margin-bottom: clamp(2px, 0.5vh, 4px);
        line-height: 1.2;
      }
      
      .voice-status-subtitle {
        font-size: clamp(10px, 2vh, 14px);
        color: ${statusSubtitleColor};
        line-height: 1.2;
      }
      
      /* Start Call Button - Scales with container */
      .start-call-btn {
        margin: 0;
        width: min(280px, 70vw)${important};
        height: clamp(48px, 8vh, 64px)${important};
        border-radius: clamp(24px, 4vh, 32px)${important};
        border: none${important};
        background: ${startCallBtnColor}${important};
        color: ${startCallBtnTextColor}${important};
        font-size: clamp(14px, 2.5vh, 18px)${important};
        font-weight: 600${important};
        cursor: pointer${important};
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        gap: clamp(8px, 1.5vh, 12px);
        box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
        transition: all 0.3s ease;
        flex-shrink: 0;
      }
      
      .start-call-btn svg {
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        width: auto${important};
        height: auto${important};
      }
      
      .start-call-btn svg {
        width: clamp(22px, 4vh, 32px);
        height: clamp(22px, 4vh, 32px);
      }
      
      .start-call-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 28px rgba(102, 126, 234, 0.5);
      }
      
      .start-call-btn:active {
        transform: translateY(-1px);
      }
      .start-call-btn:disabled {
        cursor: not-allowed${important};
        opacity: 0.6${important};
      }
      
      /* Connecting Spinner */
      .connecting-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: ${startCallBtnTextColor};
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-right: 8px;
        vertical-align: middle;
      }
      
      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      
      /* Voice Transcript - Scales with container */
      .voice-transcript {
        background: ${transcriptBg};
        padding: clamp(6px, 1.5vh, 12px);
        border-radius: 10px;
        width: min(360px, calc(100% - 40px));
        margin: 0;
        min-height: clamp(80px, 15vh, 120px);
        max-height: clamp(100px, 18vh, 140px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        flex-shrink: 1;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
      }
      
      .transcript-label {
        font-size: clamp(8px, 1.4vh, 11px);
        color: ${transcriptLabelColor};
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin-bottom: clamp(3px, 0.6vh, 6px);
        flex-shrink: 0;
        word-wrap: break-word;
      }
      
      .transcript-text {
        font-size: clamp(11px, 2vh, 15px);
        color: ${transcriptTextColor};
        line-height: 1.4;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      
      .transcript-text.empty {
        color: #cbd5e1;
        font-style: italic;
      }
      
      /* Voice Text Input Area - Type while on voice call */
      .voice-text-input-area {
        width: 100%;
        padding: 14px 16px;
        margin: 0;
        flex-shrink: 0;
        background: #ffffff;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        box-sizing: border-box;
      }
      
      .voice-input-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
        background: #f1f5f9;
        border-radius: 12px;
        padding: 4px 4px 4px 14px;
        border: 1px solid transparent;
        transition: all 0.2s ease;
      }
      
      .voice-input-wrapper:focus-within {
        background: #fff;
        border-color: #a855f7;
        box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.1);
      }
      
      .voice-text-input {
        flex: 1;
        border: none;
        outline: none;
        font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 14px;
        color: ${liveTranscriptTextColor}${important};
        background: transparent;
        padding: 10px 0;
      }
      
      .voice-text-input::placeholder {
        color: #94a3b8;
      }
      
      .voice-send-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 10px;
        border: none;
        background: ${voiceSendButtonColor}${important};
        color: #fff;
        cursor: pointer;
        transition: transform 0.2s ease;
        flex-shrink: 0;
      }
      
      .voice-send-btn:hover:not(:disabled) {
        transform: scale(1.05);
      }
      
      .voice-send-btn:active:not(:disabled) {
        transform: scale(0.95);
      }
      
      .voice-send-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }
      
      .voice-send-btn svg {
        width: 16px;
        height: 16px;
      }
      
      .voice-input-hint {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        margin-top: 10px;
        font-size: 11px;
        color: #64748b;
        transition: color 0.2s ease;
      }
      
      .voice-input-hint svg {
        width: 12px;
        height: 12px;
        color: #10b981;
      }
      
      /* Text input status states */
      .voice-input-hint--sending {
        color: #666;
      }
      
      .voice-input-hint--success {
        color: #28a745;
      }
      
      .voice-input-hint--queued {
        color: #ffc107;
      }
      
      .voice-input-hint--error {
        color: #dc3545;
      }
      
      /* Disabled input styling */
      .voice-text-input:disabled,
      #mobileTextInput:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
      }
      
      .voice-send-btn:disabled,
      #mobileSendBtn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      /* Voice Controls - Scales with container */
      .voice-controls {
        display: flex;
        gap: clamp(8px, 1.8vh, 14px);
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin: 0;
        padding-top: 0;
      }
      
      .voice-control-btn {
        width: clamp(48px, 9vh, 72px)${important};
        height: clamp(48px, 9vh, 72px)${important};
        min-width: clamp(48px, 9vh, 72px)${important};
        min-height: clamp(48px, 9vh, 72px)${important};
        max-width: clamp(48px, 9vh, 72px)${important};
        max-height: clamp(48px, 9vh, 72px)${important};
        aspect-ratio: 1${important};
        border-radius: 50%${important};
        border: none${important};
        cursor: pointer${important};
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        transition: all 0.2s;
        position: relative${important};
        flex-shrink: 0;
        overflow: hidden${important};
        padding: 0${important};
        margin: 0${important};
        box-sizing: border-box${important};
      }
      
      .voice-control-btn svg {
        width: 48%${important};
        height: 48%${important};
        min-width: 48%${important};
        min-height: 48%${important};
        max-width: 48%${important};
        max-height: 48%${important};
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        margin: 0${important};
        padding: 0${important};
        position: relative${important};
        flex-shrink: 0${important};
      }
      
      .voice-control-btn.primary {
        width: clamp(54px, 10vh, 80px)${important};
        height: clamp(54px, 10vh, 80px)${important};
        min-width: clamp(54px, 10vh, 80px)${important};
        min-height: clamp(54px, 10vh, 80px)${important};
        max-width: clamp(54px, 10vh, 80px)${important};
        max-height: clamp(54px, 10vh, 80px)${important};
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)${important};
        color: white${important};
        box-shadow: 0 6px 18px rgba(102, 126, 234, 0.4);
      }
      
      .voice-control-btn.primary:hover {
        transform: scale(1.05);
      }
      
      .voice-control-btn.primary.active {
        background: ${endCallBtnColor}${important};
        animation: recordingPulse 1.5s ease-in-out infinite;
      }
      .voice-control-btn.primary.active svg {
        fill: white${important};
      }
      
      /* More specific selectors for WordPress compatibility */
      #endCallBtn.voice-control-btn.primary.active,
      #muteBtn.voice-control-btn.secondary,
      #speakerBtn.voice-control-btn.secondary {
        border-radius: 50% !important;
        width: clamp(48px, 9vh, 72px) !important;
        height: clamp(48px, 9vh, 72px) !important;
        min-width: clamp(48px, 9vh, 72px) !important;
        min-height: clamp(48px, 9vh, 72px) !important;
        max-width: clamp(48px, 9vh, 72px) !important;
        max-height: clamp(48px, 9vh, 72px) !important;
        aspect-ratio: 1 !important;
        overflow: hidden !important;
      }
      
      #endCallBtn.voice-control-btn.primary.active {
        width: clamp(54px, 10vh, 80px) !important;
        height: clamp(54px, 10vh, 80px) !important;
        min-width: clamp(54px, 10vh, 80px) !important;
        min-height: clamp(54px, 10vh, 80px) !important;
        max-width: clamp(54px, 10vh, 80px) !important;
        max-height: clamp(54px, 10vh, 80px) !important;
      }
      
      
      /* Microphone button specific background color */
      #muteBtn.voice-control-btn.secondary {
        background: ${micButtonColor}${important};
      }
      
      /* Speaker button specific background color */
      #speakerBtn.voice-control-btn.secondary {
        background: ${speakerButtonColor}${important};
      }
      
      #endCallBtn.voice-control-btn.primary.active svg,
      #muteBtn.voice-control-btn.secondary svg,
      #speakerBtn.voice-control-btn.secondary svg {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        width: 48% !important;
        height: 48% !important;
        margin: 0 !important;
        padding: 0 !important;
        position: relative !important;
      }
      
      @keyframes recordingPulse {
        0%, 100% { 
          box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
        }
        50% { 
          box-shadow: 0 6px 24px rgba(239, 68, 68, 0.7);
        }
      }
      
      .voice-control-btn.secondary {
        background: ${controlBtnColor}${important};
        color: ${controlBtnSecondaryColor}${important};
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        width: clamp(48px, 9vh, 72px)${important};
        height: clamp(48px, 9vh, 72px)${important};
        min-width: clamp(48px, 9vh, 72px)${important};
        min-height: clamp(48px, 9vh, 72px)${important};
        max-width: clamp(48px, 9vh, 72px)${important};
        max-height: clamp(48px, 9vh, 72px)${important};
      }
      
      .voice-control-btn.secondary:hover {
        background: #f8fafc${important};
        transform: scale(1.05);
      }
      
      .voice-control-btn.secondary.muted {
        background: #f3f4f6${important};
      }
      
      .voice-control-btn.secondary.muted .mute-icon {
        stroke: #9ca3af !important;
      }
      
      .voice-control-btn.secondary svg {
        width: 48%${important};
        height: 48%${important};
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        margin: 0${important};
        padding: 0${important};
        position: relative${important};
      }
      
      .voice-timer {
        position: absolute;
        bottom: clamp(-18px, -3vh, -24px);
        font-size: clamp(9px, 1.6vh, 12px);
        color: #64748b;
        font-weight: 500;
        white-space: nowrap;
      }
      
      /* ===== DESKTOP VOICE SECTION - New Design ===== */
      .desktop-voice-section {
        padding: 16px 20px;
        background: linear-gradient(180deg, #ffffff 0%, rgba(168, 85, 247, 0.04) 100%);
        width: 100%;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        box-sizing: border-box;
        text-align: center;
      }
      
      /* Ensure all children are centered */
      .desktop-voice-section > * {
        margin-left: auto;
        margin-right: auto;
      }
      
      .desktop-timer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 5px 10px;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 20px;
        font-family: 'JetBrains Mono', 'Courier New', monospace;
        font-size: 11px;
        color: #64748b;
        margin: 0 auto;
      }
      
      .desktop-timer .timer-dot {
        width: 5px;
        height: 5px;
        background: #ef4444;
        border-radius: 50%;
        animation: timerPulse 1s ease-in-out infinite;
      }
      
      @keyframes timerPulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
      
      .desktop-avatar-container {
        margin-bottom: 12px;
      }
      
      .desktop-avatar-large {
        width: 80px !important;
        height: 80px !important;
        border-radius: 20px !important;
        margin: 0 auto 12px !important;
        box-shadow: 0 8px 24px rgba(168, 85, 247, 0.35) !important;
        background: linear-gradient(135deg, ${avatarBg} 0%, ${avatarActiveBg} 100%) !important;
      }
      
      .desktop-waveform {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        height: 36px;
        width: 100%;
      }
      
      .desktop-waveform-bar {
        width: 4px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 2px;
        animation: waveformAnimation 0.8s ease-in-out infinite;
      }
      
      .desktop-waveform-bar[data-index="0"] { height: 14px; animation-delay: 0s; }
      .desktop-waveform-bar[data-index="1"] { height: 26px; animation-delay: 0.1s; }
      .desktop-waveform-bar[data-index="2"] { height: 36px; animation-delay: 0.2s; }
      .desktop-waveform-bar[data-index="3"] { height: 22px; animation-delay: 0.3s; }
      .desktop-waveform-bar[data-index="4"] { height: 16px; animation-delay: 0.4s; }
      
      @keyframes waveformAnimation {
        0%, 100% { transform: scaleY(0.5); }
        50% { transform: scaleY(1); }
      }
      
      .desktop-waveform.paused .desktop-waveform-bar {
        animation-play-state: paused;
      }

      /* New main waveform (used in updated template) */
      .desktop-main-waveform {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        height: 48px;
        width: 100%;
        max-width: 200px;
        margin: 0 auto;
        padding: 0 20px;
      }

      .desktop-main-waveform .waveform-bar {
        width: 3px;
        background: #a855f7;
        border-radius: 2px;
        height: 8px;
        animation: waveformAnimation 0.8s ease-in-out infinite;
      }

      .desktop-main-waveform .waveform-bar:nth-child(1) { height: 12px; animation-delay: 0s; }
      .desktop-main-waveform .waveform-bar:nth-child(2) { height: 20px; animation-delay: 0.05s; }
      .desktop-main-waveform .waveform-bar:nth-child(3) { height: 28px; animation-delay: 0.1s; }
      .desktop-main-waveform .waveform-bar:nth-child(4) { height: 36px; animation-delay: 0.15s; }
      .desktop-main-waveform .waveform-bar:nth-child(5) { height: 44px; animation-delay: 0.2s; }
      .desktop-main-waveform .waveform-bar:nth-child(6) { height: 50px; animation-delay: 0.25s; }
      .desktop-main-waveform .waveform-bar:nth-child(7) { height: 54px; animation-delay: 0.3s; }
      .desktop-main-waveform .waveform-bar:nth-child(8) { height: 56px; animation-delay: 0.35s; }
      .desktop-main-waveform .waveform-bar:nth-child(9) { height: 54px; animation-delay: 0.4s; }
      .desktop-main-waveform .waveform-bar:nth-child(10) { height: 50px; animation-delay: 0.45s; }
      .desktop-main-waveform .waveform-bar:nth-child(11) { height: 44px; animation-delay: 0.5s; }
      .desktop-main-waveform .waveform-bar:nth-child(12) { height: 36px; animation-delay: 0.55s; }
      .desktop-main-waveform .waveform-bar:nth-child(13) { height: 28px; animation-delay: 0.6s; }
      .desktop-main-waveform .waveform-bar:nth-child(14) { height: 20px; animation-delay: 0.65s; }
      .desktop-main-waveform .waveform-bar:nth-child(15) { height: 12px; animation-delay: 0.7s; }

      .desktop-status {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        font-size: 12px;
        color: ${statusTitleColor};
        font-weight: 500;
        width: 100%;
        text-align: center;
        margin: 0 auto;
      }
      
      .desktop-status .status-dot {
        width: 5px;
        height: 5px;
        background: ${statusDotColor};
        border-radius: 50%;
        animation: statusPulse 1.5s ease-in-out infinite;
      }
      
      @keyframes statusPulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.15); opacity: 0.8; }
      }
      
      .desktop-live-waveform {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        height: 48px;
        margin-bottom: 20px;
      }
      
      .desktop-live-bar {
        width: 3px;
        background: linear-gradient(180deg, ${avatarBg}, ${avatarActiveBg});
        border-radius: 2px;
        transition: height 0.1s ease;
        height: 8px;
      }
      
      .desktop-controls {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin: 0 auto;
      }
      
      .desktop-control-btn {
        width: 46px;
        height: 46px;
        border-radius: 50%;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .desktop-control-btn svg {
        width: 20px;
        height: 20px;
      }
      
      .desktop-mic-icon,
      .desktop-speaker-icon {
        width: 20px !important;
        height: 20px !important;
        display: block;
        flex-shrink: 0;
      }
      
      .desktop-control-btn.secondary.muted .desktop-mic-icon,
      .desktop-control-btn.secondary.muted .desktop-speaker-icon {
        width: 20px !important;
        height: 20px !important;
      }
      
      
      .desktop-control-btn.secondary:hover {
        background: rgba(0, 0, 0, 0.1);
        color: #1e293b;
        transform: scale(1.05);
      }
      .desktop-control-btn.secondary.muted {
        background: #f3f4f6;
        color: #9ca3af;
      }
      
      .desktop-control-btn.secondary.muted .desktop-mic-icon,
      .desktop-control-btn.secondary.muted .desktop-speaker-icon {
        stroke: #9ca3af;
      }
      
      .desktop-control-btn.secondary {
        position: relative;
      }
      
      
      /* Desktop microphone button background color */
      #desktopMuteBtn.desktop-control-btn.secondary,
      #desktopMuteBtnCompact.compact-control-btn.secondary {
        background: ${micButtonColor}${important};
      }
      
      /* Desktop speaker button background color */
      #desktopSpeakerBtn.desktop-control-btn.secondary,
      #desktopSpeakerBtnCompact.compact-control-btn.secondary {
        background: ${speakerButtonColor}${important};
      }
      .desktop-mic-off-icon,
      .desktop-speaker-off-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 46px;
        height: 46px;
        transform: translate(-50%, -50%);
        z-index: 10;
      }
      .desktop-control-btn.danger {
        width: 50px;
        height: 50px;
        background: ${endCallBtnColor}${important};
        color: #ffffff;
        box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
        overflow: visible;
      }
      
      .desktop-control-btn.danger svg {
        width: 20px;
        height: 20px;
        overflow: visible;
      }
      
      .desktop-control-btn.danger:hover {
        transform: scale(1.06);
        box-shadow: 0 6px 20px rgba(239, 68, 68, 0.5);
      }
      
      .desktop-control-btn.danger:hover {
        transform: scale(1.06);
        box-shadow: 0 6px 20px rgba(239, 68, 68, 0.5);
      }
      
      
      /* ===== COMPACT VOICE SECTION (Single-row layout) ===== */
      .compact-voice-section {
        padding: 10px 14px;
        background: linear-gradient(180deg, #ffffff, rgba(168, 85, 247, 0.04));
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        min-height: 62px;
        flex-shrink: 0;
      }
      
      .compact-left {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      .compact-avatar {
        width: 42px;
        height: 42px;
        min-width: 42px;
        min-height: 42px;
        border-radius: 12px;
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        overflow: visible;
        position: relative;
      }
      
      /* Waveform container in compact avatar - ensure it's visible */
      .compact-avatar .desktop-main-waveform {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        height: 100%;
        width: 100%;
        padding: 4px;
        transform: scale(0.75);
        transform-origin: center;
      }
      
      /* Ensure waveform bars are visible and animated in compact mode */
      .compact-avatar .desktop-main-waveform .waveform-bar {
        width: 2px;
        min-width: 2px;
        background: #a855f7;
        border-radius: 1px;
        flex-shrink: 0;
        animation: waveformAnimation 0.8s ease-in-out infinite;
      }
      
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(1) { height: 6px; animation-delay: 0s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(2) { height: 10px; animation-delay: 0.05s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(3) { height: 14px; animation-delay: 0.1s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(4) { height: 18px; animation-delay: 0.15s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(5) { height: 22px; animation-delay: 0.2s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(6) { height: 26px; animation-delay: 0.25s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(7) { height: 28px; animation-delay: 0.3s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(8) { height: 30px; animation-delay: 0.35s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(9) { height: 28px; animation-delay: 0.4s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(10) { height: 26px; animation-delay: 0.45s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(11) { height: 22px; animation-delay: 0.5s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(12) { height: 18px; animation-delay: 0.55s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(13) { height: 14px; animation-delay: 0.6s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(14) { height: 10px; animation-delay: 0.65s; }
      .compact-avatar .desktop-main-waveform .waveform-bar:nth-child(15) { height: 6px; animation-delay: 0.7s; }

      }
      
      .compact-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      
      .compact-timer {
        display: flex;
        align-items: center;
        gap: 5px;
        font-family: 'JetBrains Mono', 'Courier New', monospace;
        font-size: 13px;
        font-weight: 600;
        color: ${liveTranscriptTextColor}${important};
      }
      
      .compact-timer .timer-dot {
        width: 6px;
        height: 6px;
        background: #ef4444;
        border-radius: 50%;
        animation: timerPulse 1s ease-in-out infinite;
      }
      
      .compact-status {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: ${statusTitleColor};
      }
      
      .compact-status .status-dot {
        width: 5px;
        height: 5px;
        background: ${statusDotColor};
        border-radius: 50%;
        animation: statusPulse 1.5s ease-in-out infinite;
      }
      
      @keyframes statusPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.1); }
      }
      
      .compact-controls {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      
      .compact-control-btn {
        width: 36px;
        height: 36px;
        min-width: 36px;
        min-height: 36px;
        border-radius: 50%;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        padding: 0;
        position: relative;
      }
      
      .compact-control-btn svg {
        width: 16px;
        height: 16px;
      }
      
      .compact-control-btn.secondary {
        background: #f1f5f9;
        color: #64748b;
      }
      
      
      /* Compact speaker button background color */
      #desktopSpeakerBtnCompact.compact-control-btn.secondary {
        background: ${speakerButtonColor}${important};
      }
      /* Compact microphone button background color */
      #desktopMuteBtnCompact.compact-control-btn.secondary {
        background: ${micButtonColor}${important};
      }
      
      .compact-control-btn.secondary:hover {
        background: #e2e8f0;
        color: #475569;
      }
      
      .compact-control-btn.secondary.muted {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      }
      
      
      .compact-control-btn.secondary.muted .desktop-mic-icon,
      .compact-control-btn.secondary.muted .desktop-speaker-icon {
        stroke: #ef4444;
      }
      
      .compact-control-btn.secondary.muted .desktop-mic-off-icon,
      .compact-control-btn.secondary.muted .desktop-speaker-off-icon {
        width: 46px;
        height: 46px;
      }

      .compact-control-btn.danger {
        width: 40px;
        height: 40px;
        min-width: 40px;
        min-height: 40px;
        background: ${endCallBtnColor}${important};
        color: white;
        box-shadow: 0 3px 10px rgba(239, 68, 68, 0.35);
      }
      
      .compact-control-btn.danger:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 14px rgba(239, 68, 68, 0.45);
      }
      
      .compact-control-btn.danger svg {
        width: 18px;
        height: 18px;
        overflow: visible;
      }
      
/* ===== CONVERSATION PANEL ===== */
      .desktop-conversation-panel {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        width: 100%;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
        flex: 1;
      }
      
      .conversation-header {
        padding: 10px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #fafafa;
        cursor: pointer;
        flex-shrink: 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.04);
        transition: background 0.2s ease;
      }
      
      .conversation-header:hover {
        background: #f5f5f5;
      }
      
      .conversation-label {
        font-size: 11px;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .conversation-toggle {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: #94a3b8;
        font-weight: 500;
      }
      
      .conversation-toggle svg {
        transition: transform 0.2s ease;
      }
      
      .conversation-header.expanded .conversation-toggle svg {
        transform: rotate(180deg);
      }
      
      /* ===== COLLAPSED: Live Transcript Only (2 lines) ===== */
      .live-transcript-collapsed {
        padding: 12px 16px;
        background: #fafafa;
        display: flex;
        flex-direction: column;
        gap: 6px;
        flex-shrink: 0;
        min-height: 50px;
      }
      
      .live-indicator {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 9px;
        font-weight: 700;
        color: ${liveIndicatorTextColor}${important};
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      
      .live-indicator .live-dot {
        height: 5px;
        width: 5px;
        background: ${liveIndicatorDotColor}${important};
        border-radius: 50%;
        animation: statusPulse 1.5s ease-in-out infinite;
      }
      
      .live-text-collapsed {
        font-size: ${liveTranscriptFontSize}${important};
        color: ${liveTranscriptTextColor}${important};
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      
      .live-text-collapsed.muted {
        color: #94a3b8;
        font-style: italic;
      }
      
      .typing-cursor {
        display: inline-block;
        width: 2px;
        height: 14px;
        background: #a855f7;
        margin-left: 2px;
        animation: blink 1s step-end infinite;
        vertical-align: middle;
      }
      
      @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }
      
      /* ===== EXPANDED: Full Conversation History ===== */
      .conversation-history {
        flex: 1;
        flex-direction: column;
        gap: 10px;
        overflow-y: auto;
        padding: 14px 16px;
        min-height: 0;
      }
      .conversation-history.visible {
        display: flex;
      }

      .conversation-messages {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      /* Message Rows */
      .message-row {
        display: flex;
        gap: 8px;
        animation: msgIn 0.25s ease;
      }
      
      @keyframes msgIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes slideOut {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(-10px); }
      }
      
      .message-row.user {
        flex-direction: row-reverse;
      }
      
      .message-avatar {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .message-avatar.assistant {
        background: linear-gradient(135deg, #c084fc, #a855f7);
      }

      .message-avatar.assistant .orb {
        width: 12px;
        height: 12px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 50%;
      }

      .message-avatar.user {
        background: linear-gradient(135deg, #64748b, #475569);
        color: #fff;
        font-size: 11px;
        font-weight: 600;
      }

      .message-bubble {
        max-width: 80%;
        padding: 9px 13px;
        border-radius: 12px;
        font-size: 13px;
        line-height: 1.5;
      }
      
      .message-row.assistant .message-bubble {
        background: #fff;
        color: ${liveTranscriptTextColor}${important};
        border: 1px solid rgba(0, 0, 0, 0.06);
        border-bottom-left-radius: 4px;
      }
      
      .message-row.user .message-bubble {
        background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
        color: #fff;
        border-bottom-right-radius: 4px;
      }
      
      /* Live message at bottom of history */
      .live-message-row {
        display: flex;
        gap: 8px;
        padding-top: 8px;
        border-top: 1px dashed rgba(0, 0, 0, 0.08);
        margin-top: auto;
      }
      
      .live-message-row .message-bubble {
        background: #fff;
        border: 1px solid rgba(16, 185, 129, 0.3);
        color: ${liveTranscriptTextColor}${important};
        position: relative;
        border-radius: 14px;
        border-bottom-left-radius: 4px;
      }
      
      .live-message-row .live-badge {
        position: absolute;
        top: -8px;
        left: 10px;
        background: #10b981;
        color: #fff;
        font-size: 8px;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      /* Hide/show based on expanded state */
      .desktop-conversation-panel.collapsed .conversation-history {
        display: none;
      }
      
      .desktop-conversation-panel.collapsed .live-transcript-collapsed {
        display: flex;
      }
      
      .desktop-conversation-panel.expanded .conversation-history {
        display: flex;
        max-height: 400px;
      }
      
      .desktop-conversation-panel.expanded .live-transcript-collapsed {
        display: none;
      }
      
            /* ===== DARK MODE SUPPORT ===== */
      @media (prefers-color-scheme: dark) {
        .desktop-voice-section {
          background: linear-gradient(180deg, #1e1e2e 0%, rgba(168, 85, 247, 0.08) 100%);
        }

        .desktop-timer {
          color: #94a3b8;
        }

        .desktop-status {
          color: #34d399;
        }

        .desktop-status .status-dot {
      
      .desktop-control-btn.secondary.muted {
        background: rgba(255, 255, 255, 0.1);
        color: #9ca3af;
      
      .compact-voice-section {
        background: linear-gradient(180deg, #1e1e2e 0%, rgba(168, 85, 247, 0.08) 100%);
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      }
      
      .compact-timer {
        color: #e2e8f0;
      }
      
      .compact-status {
        color: #34d399;
      }
      
      .compact-status .status-dot {
        background: #34d399;
      }
      
      .compact-control-btn.secondary {
        background: rgba(255, 255, 255, 0.08);
        color: #94a3b8;
      }
      
      .compact-control-btn.secondary:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #e2e8f0;
      }
      
      .compact-control-btn.secondary.muted {
        background: rgba(239, 68, 68, 0.2);
        color: #f87171;
      }

      }
      
      .desktop-control-btn.secondary.muted svg {
      
      .desktop-control-btn.secondary {
        position: relative;
      }
      
      /* Desktop microphone button background color */
      #desktopMuteBtn.desktop-control-btn.secondary,
      #desktopMuteBtnCompact.compact-control-btn.secondary {
        background: ${micButtonColor}${important};
      }
      
      .desktop-mic-off-icon,
      .desktop-speaker-off-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 20px;
        height: 20px;
      }
      
      .desktop-control-btn.secondary.muted {
        background: #f3f4f6;
        color: #9ca3af;
      }
      
      .desktop-control-btn.secondary.muted svg {
      
      .desktop-control-btn.secondary {
        position: relative;
      }
      
      /* Desktop microphone button background color */
      #desktopMuteBtn.desktop-control-btn.secondary,
      #desktopMuteBtnCompact.compact-control-btn.secondary {
        background: ${micButtonColor}${important};
      }
      
      .desktop-mic-off-icon,
      .desktop-speaker-off-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 20px;
        height: 20px;
      }
        stroke: #9ca3af;
      }
        stroke: #9ca3af;
      }
          background: #34d399;
        }

        .desktop-control-btn.secondary {
          background: rgba(255, 255, 255, 0.08);
          color: #94a3b8;
        }

        .desktop-control-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #e2e8f0;
        }

        .desktop-conversation-panel {
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }

        .conversation-header {
          background: #1a1a2e;
        }

        .conversation-header:hover {
          background: #252540;
        }

        .conversation-label {
          color: #94a3b8;
        }

        .conversation-toggle {
          color: #64748b;
        }

        .live-transcript-preview {
          background: #1a1a2e;
        }
        .live-indicator {
          color: ${liveIndicatorTextColor}${important};
        }
        .live-indicator .live-dot {
          background: ${liveIndicatorDotColor}${important};
        }

        .live-text {
          color: #e2e8f0;
        }

        .live-text.muted {
          color: #64748b;
        }

        .conversation-history {
          background: #1a1a2e;
        }

        .conversation-msg.assistant .conversation-msg-bubble {
          background: #252540;
          color: #e2e8f0;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .voice-text-input-area {
          background: #1a1a2e;
        }

        .voice-text-input {
          background: #252540 !important;
          color: #e2e8f0 !important;
          border-color: rgba(255, 255, 255, 0.1) !important;
        }

        .voice-text-input::placeholder {
          color: #64748b !important;
        }

        .voice-text-input:focus {
          border-color: ${avatarBg} !important;
          box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.2) !important;
        }

        .voice-input-hint {
          color: #64748b !important;
        }

        .voice-send-btn {
          background: ${voiceSendButtonColor} !important;
        }

        .voice-send-btn:disabled {
          background: #374151 !important;
          color: #4b5563 !important;
        }
      }

      /* Mobile optimization */
      @media (max-width: 768px) {
        .voice-interface {
          padding: 6px 10px;
        }
      }
      
      @media (max-width: 480px) {
        .voice-interface {
          padding: 5px 8px;
        }
      }
      
      /* Mobile Voice Call UI - Shared styles (also used in generateMobileCSS) */
      ${this._getSharedMobileCSS(important)}
    `;
  }

  /**
   * Get shared mobile CSS used by both generateCSS and generateMobileCSS
   * @private
   */
  _getSharedMobileCSS(important) {
    return `
      .mobile-duration-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: #ef4444;
        animation: mobilePulse 1.5s ease-in-out infinite;
      }

      @keyframes mobilePulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.95); }
      }

      .mobile-voice-bar {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 14px;
        background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%);
        border-radius: 24px 24px 0 0;
        box-shadow: 0 -8px 32px rgba(124, 58, 237, 0.5), 0 -2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2);
        width: 100%;
        max-width: 100%;
        pointer-events: auto;
      }

      .mobile-top-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        position: relative;
      }

      .mobile-status-indicator {
        display: flex;
        align-items: center;
        gap: 5px;
        min-width: 85px;
      }

      .mobile-status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: #22c55e;
        box-shadow: 0 0 8px #22c55e;
        transition: all 0.3s ease;
      }

      .mobile-status-text {
        font-size: 11px;
        font-weight: 700;
        color: rgba(255,255,255,0.95);
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .mobile-waveform-center {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        height: 24px;
      }

      .mobile-waveform-bar {
        width: 4px;
        height: 4px;
        background-color: #fff;
        border-radius: 2px;
        transition: height 0.15s ease;
        box-shadow: 0 0 8px rgba(255,255,255,0.5);
      }

      .mobile-controls {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .mobile-control-btn {
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        width: 32px${important};
        height: 32px${important};
        min-width: 32px${important};
        min-height: 32px${important};
        max-width: 32px${important};
        max-height: 32px${important};
        border-radius: 50%${important};
        border: none${important};
        cursor: pointer${important};
        transition: all 0.2s ease;
        background-color: rgba(255,255,255,0.15)${important};
        position: relative${important};
        overflow: hidden${important};
        padding: 0${important};
        margin: 0${important};
        box-sizing: border-box${important};
      }

      .mobile-control-btn:hover {
        background-color: rgba(255,255,255,0.25)${important};
      }

      .mobile-control-btn.muted {
        background-color: rgba(239, 68, 68, 0.3)${important};
      }

      .mobile-control-btn svg {
        position: absolute${important};
        top: 50%${important};
        left: 50%${important};
        transform: translate(-50%, -50%)${important};
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        width: 15px${important};
        height: 15px${important};
        margin: 0${important};
        padding: 0${important};
      }

      .mobile-mic-off-icon,
      .mobile-speaker-off-icon {
        display: none !important;
      }

      .mobile-control-btn.muted .mobile-mic-icon,
      .mobile-control-btn.muted .mobile-speaker-icon {
        display: none !important;
      }

      .mobile-control-btn.muted .mobile-mic-off-icon,
      .mobile-control-btn.muted .mobile-speaker-off-icon {
        display: block !important;
      }

      .mobile-end-call-btn {
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        width: 36px${important};
        height: 36px${important};
        min-width: 36px${important};
        min-height: 36px${important};
        max-width: 36px${important};
        max-height: 36px${important};
        border-radius: 50%${important};
        border: none${important};
        cursor: pointer${important};
        background-color: #ef4444${important};
        box-shadow: 0 4px 14px rgba(239, 68, 68, 0.5);
        transition: all 0.2s ease;
        overflow: hidden${important};
        padding: 0${important};
        margin: 0${important};
        box-sizing: border-box${important};
      }

      .mobile-end-call-btn svg {
        display: block${important};
        visibility: visible${important};
        opacity: 1${important};
        width: 15px${important};
        height: 15px${important};
        margin: 0${important};
        padding: 0${important};
        position: relative${important};
      }

      /* ID-specific selectors for WordPress compatibility */
      #mobileMuteBtn.mobile-control-btn,
      #mobileSpeakerBtn.mobile-control-btn,
      #mobileEndCallBtn.mobile-end-call-btn {
        border-radius: 50% !important;
        overflow: hidden !important;
      }

      #mobileMuteBtn.mobile-control-btn,
      #mobileSpeakerBtn.mobile-control-btn {
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
      }

      #mobileEndCallBtn.mobile-end-call-btn {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        max-width: 36px !important;
        max-height: 36px !important;
      }

      #mobileMuteBtn.mobile-control-btn svg,
      #mobileSpeakerBtn.mobile-control-btn svg,
      #mobileEndCallBtn.mobile-end-call-btn svg {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        width: 15px !important;
        height: 15px !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .mobile-end-call-btn:hover {
        transform: scale(1.05);
      }

      .mobile-transcript-row {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 10px 12px;
        background: rgba(255,255,255,0.18);
        border-radius: 14px;
        border: none;
        cursor: pointer;
        text-align: left;
        backdrop-filter: blur(4px);
        transition: background 0.2s ease;
      }

      .mobile-transcript-row:hover {
        background: rgba(255,255,255,0.25);
      }

      .mobile-transcript-text {
        margin: 0;
        font-size: 13px;
        color: #fff;
        line-height: 1.4;
        font-weight: 500;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .mobile-transcript-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .mobile-expand-hint {
        font-size: 10px;
        color: rgba(255,255,255,0.6);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 600;
      }

      .mobile-transcript-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0,0,0,0.6);
        display: flex;
        align-items: flex-end;
        padding: 12px;
        z-index: 100000;
        pointer-events: auto;
      }

      .mobile-expanded-transcript {
        width: 100%;
        max-height: 75%;
        background: #fff;
        border-radius: 24px 24px 16px 16px;
        overflow: hidden;
        box-shadow: 0 -8px 40px rgba(0,0,0,0.3);
        display: flex;
        flex-direction: column;
      }

      .mobile-transcript-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 14px 16px;
        background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
        flex-shrink: 0;
      }

      .mobile-header-left {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .mobile-header-status {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .mobile-header-status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: #22c55e;
        box-shadow: 0 0 6px #22c55e;
      }

      .mobile-header-status-text {
        font-size: 10px;
        font-weight: 700;
        color: rgba(255,255,255,0.9);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .mobile-header-duration {
        font-size: 10px;
        font-weight: 600;
        color: rgba(255,255,255,0.7);
        margin-left: 8px;
      }

      .mobile-transcript-label {
        font-size: 11px;
        font-weight: 800;
        color: rgba(255,255,255,0.7);
        letter-spacing: 1px;
      }

      .mobile-close-transcript-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: rgba(255,255,255,0.2);
        color: #fff;
        cursor: pointer;
      }

      .mobile-close-transcript-btn:hover {
        background: rgba(255,255,255,0.3);
      }

      .mobile-messages-container {
        flex: 1;
        padding: 16px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .mobile-message-bubble {
        max-width: 85%;
        padding: 12px 16px;
        border-radius: 18px;
        font-size: 14px;
        line-height: 1.5;
        word-wrap: break-word;
      }

      .mobile-message-bubble.assistant {
        align-self: flex-start;
        background-color: #f3f4f6;
        color: #374151;
        border-bottom-left-radius: 4px;
      }

      .mobile-message-bubble.user {
        align-self: flex-end;
        background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
        color: #fff;
        border-bottom-right-radius: 4px;
      }

      .mobile-input-area {
        padding: 12px 16px 16px;
        border-top: 1px solid #e5e7eb;
        background: #f9fafb;
        flex-shrink: 0;
      }

      .mobile-input-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 4px 4px 16px;
        background: #fff;
        border-radius: 24px;
        border: 2px solid #e5e7eb;
      }

      .mobile-text-input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 14px;
        color: #374151;
        background: transparent;
        padding: 10px 0;
      }

      .mobile-text-input::placeholder {
        color: #9ca3af;
      }

      .mobile-send-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: none;
        background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
        color: #fff;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .mobile-send-btn:hover:not(:disabled) {
        transform: scale(1.05);
      }

      .mobile-send-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .mobile-input-hint {
        margin: 8px 0 0;
        font-size: 11px;
        color: #9ca3af;
        text-align: center;
        transition: color 0.2s ease;
      }

      /* Mobile text input status states */
      .mobile-input-hint--sending {
        color: #666;
      }

      .mobile-input-hint--success {
        color: #28a745;
      }

      .mobile-input-hint--queued {
        color: #ffc107;
      }

      .mobile-input-hint--error {
        color: #dc3545;
      }

      /* Disabled mobile input styling */
      #mobileTextInput:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
      }

      #mobileSendBtn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
  }

  /**
   * Generate mobile-specific CSS for minimized bar (injected into document.head)
   */
  generateMobileCSS() {
    const important = this.config.useShadowDOM === false ? ' !important' : '';

    return `
      /* Mobile Voice Call UI - Container positioning */
      #mobile-voice-call-bar-container {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 99999;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        padding: 0 12px 12px;
        pointer-events: none;
      }

      .mobile-duration-badge {
        position: absolute;
        top: -28px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 5px 14px;
        background: rgba(0,0,0,0.75);
        border-radius: 20px;
        font-size: 12px;
        font-weight: 700;
        color: #fff;
        backdrop-filter: blur(8px);
        z-index: 10;
        pointer-events: auto;
      }

      /* Shared mobile styles */
      ${this._getSharedMobileCSS(important)}
    `;
  }
}
